import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppShell from "@/components/AppShell";
import CampaignBatchModal from "@/components/CampaignBatchModal";
import RequireAuth from "@/components/RequireAuth";
import RouteFallback from "@/components/RouteFallback";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import WorkspaceProjectProvider from "@/components/WorkspaceProjectProvider";
import ProjectsPicker from "@/pages/ProjectsPicker";
import RequireActiveProject from "@/components/RequireActiveProject";
import HomeRedirect from "@/components/HomeRedirect";
import { routeImporters } from "@/lib/preload";

const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Clients = lazy(routeImporters.clients);
const ClientDetail = lazy(routeImporters.clientDetail);
const Calendar = lazy(routeImporters.calendar);
const History = lazy(routeImporters.history);
const ScheduledCalls = lazy(routeImporters.scheduled);
const Profile = lazy(routeImporters.profile);
const Settings = lazy(routeImporters.settings);
const Notifications = lazy(routeImporters.notifications);
const WorkspaceSettings = lazy(() => import("@/pages/WorkspaceSettings"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const Billing = lazy(() => import("@/pages/Billing"));

// Phase A — admin route tree. Lives in its own lazy chunk so customer-
// only sessions never download admin code. RequireSystemAdmin gates
// the entire subtree on the user having a SystemAdmin row; AdminShell
// itself handles the MFA-routing branch.
const AdminShell = lazy(() => import("@/components/AdminShell"));
const RequireSystemAdmin = lazy(() => import("@/components/RequireSystemAdmin"));
const AdminHome = lazy(() => import("@/pages/admin/AdminHome"));
const MfaEnroll = lazy(() => import("@/pages/admin/MfaEnroll"));
const AdminAuditLog = lazy(() => import("@/pages/admin/AuditLog"));
const AdminUsersList = lazy(() => import("@/pages/admin/AdminUsersList"));
const AdminUserDetail = lazy(() => import("@/pages/admin/AdminUserDetail"));
const AdminWorkspacesList = lazy(() => import("@/pages/admin/AdminWorkspacesList"));
const AdminWorkspaceDetail = lazy(() => import("@/pages/admin/AdminWorkspaceDetail"));
const AdminHealth = lazy(() => import("@/pages/admin/AdminHealth"));
const AdminChangelog = lazy(() => import("@/pages/admin/AdminChangelog"));

export default function App() {
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* /invite/:token is intentionally OUTSIDE the auth wrapper —
            the page itself reads the preview unauth'd and then routes
            through /login when the recipient needs to sign in. */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route
          element={
            <RequireAuth>
              <WorkspaceProjectProvider>
                <AppShell />
                <CampaignBatchModal />
              </WorkspaceProjectProvider>
            </RequireAuth>
          }
        >
          {/* `/` sits OUTSIDE RequireActiveProject so a fresh user
              landing here after an MS OAuth callback can bounce to a
              stashed invitation page (HomeRedirect handles that)
              instead of being shoved at /projects first. */}
          <Route path="/" element={<HomeRedirect />} />

          {/* No-project state: project picker is always reachable.
              RequireActiveProject below redirects pages that need scope. */}
          <Route path="/projects" element={<ProjectsPicker />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/workspace/settings" element={<WorkspaceSettings />} />

          {/* Per-project pages — wrapped in RequireActiveProject so a fresh
              user with no project lands on the picker instead of hitting
              a 412 on the first API call. */}
          <Route element={<RequireActiveProject />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:clientId" element={<ClientDetail />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/history" element={<History />} />
            <Route path="/scheduled" element={<ScheduledCalls />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/billing" element={<Billing />} />
          </Route>
        </Route>

        {/* Admin tree — sibling to the customer tree above, NOT nested
            inside RequireAuth/WorkspaceProjectProvider/AppShell. The
            admin shell needs its own gate (RequireSystemAdmin) plus
            its own URL prefix (/admin) so customers can't accidentally
            land on it via a stale link. */}
        <Route
          element={
            <RequireSystemAdmin>
              <AdminShell />
            </RequireSystemAdmin>
          }
        >
          <Route path="/admin" element={<AdminHome />} />
          <Route path="/admin/mfa-enroll" element={<MfaEnroll />} />
          <Route path="/admin/audit" element={<AdminAuditLog />} />
          <Route path="/admin/users" element={<AdminUsersList />} />
          <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
          <Route path="/admin/workspaces" element={<AdminWorkspacesList />} />
          <Route path="/admin/workspaces/:wsId" element={<AdminWorkspaceDetail />} />
          <Route path="/admin/health" element={<AdminHealth />} />
          <Route path="/admin/changelog" element={<AdminChangelog />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}
